// src/pages/InvitationNotaire.jsx
// Page d'acceptation d'une invitation notaire→client. Le client arrive ici via le
// lien magique de l'email (authentifié). Il ACCEPTE explicitement de donner accès
// (principe §22.8 : l'accès résulte toujours d'une autorisation du client).
// Route /invitation-notaire?token=…
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

export default function InvitationNotaire() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");

  const [state, setState] = useState("loading"); // loading | need_login | ready | done | declined | error
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) { setState("error"); setError("Lien d'invitation invalide."); return; }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setState("need_login"); return; }
    try {
      const { data, error: err } = await supabase.rpc("get_notaire_invitation", { p_token: token });
      if (err) throw err;
      if (!data || data.status !== "pending") {
        setState("error");
        setError(data?.status === "accepted" ? "Cette invitation a déjà été acceptée." : "Cette invitation n'est plus valable.");
        return;
      }
      setInvite(data);
      setState("ready");
    } catch (e) {
      console.error("[invitation-notaire]", e);
      setState("error"); setError("Impossible de charger l'invitation.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: err } = await supabase.rpc("accept_notaire_invitation", { p_token: token });
      if (err) throw err;
      if (!data?.ok) throw new Error(data?.error || "accept_failed");
      setState("done");
    } catch (e) {
      console.error("[invitation-notaire] accept", e);
      setError(String(e.message || e) === "email_mismatch"
        ? "Cette invitation a été envoyée à une autre adresse email."
        : "L'acceptation a échoué. Réessayez.");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try { await supabase.rpc("decline_notaire_invitation", { p_token: token }); } catch { /* ignore */ }
    setState("declined"); setBusy(false);
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#f9f7f4] flex items-center justify-center px-4" style={{ fontFamily: "Georgia, serif" }}>
      <div className="w-full max-w-md bg-white border border-[#e5e0d8] rounded-2xl p-8 text-center">
        <div className="tracking-[0.15em] text-lg mb-6">ARTKIV</div>
        {children}
      </div>
    </div>
  );

  if (state === "loading") return <Shell><p className="text-[#8a8378]">Chargement…</p></Shell>;

  if (state === "need_login") return (
    <Shell>
      <h1 className="text-xl font-bold mb-3">Connexion requise</h1>
      <p className="text-sm text-[#4a453d] mb-5">Ouvrez cette page depuis le lien reçu par email — il vous connecte automatiquement. Si le lien a expiré, demandez à votre notaire de renvoyer l'invitation.</p>
      <button onClick={() => navigate("/login")} className="px-5 py-2.5 bg-[#1a1a1a] text-[#f9f7f4] rounded-lg text-sm">Se connecter</button>
    </Shell>
  );

  if (state === "error") return (
    <Shell><h1 className="text-xl font-bold mb-3">Invitation indisponible</h1><p className="text-sm text-[#4a453d]">{error}</p></Shell>
  );

  if (state === "declined") return (
    <Shell><h1 className="text-xl font-bold mb-3">Invitation refusée</h1><p className="text-sm text-[#4a453d]">Aucun accès n'a été accordé. Vous pouvez fermer cette page.</p></Shell>
  );

  if (state === "done") return (
    <Shell>
      <div className="text-2xl mb-2">✓</div>
      <h1 className="text-xl font-bold mb-3">Accès accordé</h1>
      <p className="text-sm text-[#4a453d] mb-6">Votre notaire pourra consulter votre collection en lecture. Vous gardez la main : l'accès est révocable à tout moment. Commencez à documenter vos œuvres.</p>
      <button onClick={() => navigate("/my-collection")} className="px-5 py-2.5 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-lg text-sm">Documenter ma collection</button>
    </Shell>
  );

  // ready
  return (
    <Shell>
      <div className="text-xs uppercase tracking-[0.14em] text-[#8a8378] mb-3">Invitation</div>
      <h1 className="text-xl font-bold mb-3 leading-snug">
        {invite.etude ? `${invite.notaire_nom} (${invite.etude})` : invite.notaire_nom} souhaite préparer votre dossier art
      </h1>
      <p className="text-sm text-[#4a453d] leading-relaxed mb-6">
        En acceptant, vous accordez à votre notaire un accès en <strong>lecture</strong> à votre collection, pour préparer la transmission.
        Vous restez propriétaire de vos données et pouvez révoquer cet accès à tout moment.
      </p>
      {error && <p className="text-sm text-[#dc2626] mb-4">{error}</p>}
      <div className="flex gap-3">
        <button onClick={decline} disabled={busy} className="flex-1 px-4 py-2.5 border border-[#e5e0d8] text-[#4a453d] rounded-lg text-sm hover:bg-[#f9f7f4] disabled:opacity-60">Refuser</button>
        <button onClick={accept} disabled={busy} className="flex-1 px-4 py-2.5 bg-[#1a1a1a] text-[#f9f7f4] rounded-lg text-sm hover:bg-black disabled:opacity-60">
          {busy ? "…" : "Accepter"}
        </button>
      </div>
    </Shell>
  );
}
